import {
  signInAnonymously as firebaseSignInAnonymously,
  signInWithPopup,
  GoogleAuthProvider,
  TwitterAuthProvider,
  linkWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { auth } from './firebase/config';
import { User, createDefaultUser } from '../models/User';

export class AuthService {
  private currentUser: User | null = null;

  constructor() {
    // 認証状態の監視（永続化されたセッションも検出）
    onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const authType = firebaseUser.isAnonymous ? '(匿名)' :
                        firebaseUser.providerData.some(p => p?.providerId === 'twitter.com') ? '(Twitter)' :
                        firebaseUser.providerData.some(p => p?.providerId === 'google.com') ? '(Google)' : '(その他)';
        console.log('認証状態復元: ', firebaseUser.uid, authType);
        // 一時的にFirestoreを使わず、メモリ上でユーザー管理
        this.currentUser = await this.loadUserWithStats(firebaseUser);
      } else {
        this.currentUser = null;
      }
    });
  }

  /**
   * ユーザー情報と統計を読み込む
   */
  private async loadUserWithStats(firebaseUser: FirebaseUser, nickname?: string): Promise<User> {
    const user = this.createUserFromFirebaseUser(firebaseUser, nickname);

    try {
      // Firestoreから統計情報を取得
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('./firebase/config');

      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        // 統計情報があれば上書き
        if (userData.stats) {
          user.stats = userData.stats;
        }
        // 保存されたニックネームがあれば使用（新規ニックネームより優先度低い）
        if (!nickname && userData.nickname) {
          const savedNickname = userData.nickname;
          const idSuffix = firebaseUser.uid.slice(-4);
          user.displayName = `${savedNickname} #${idSuffix}`;
        }
      }
    } catch (error) {
      console.log('Stats loading failed, using default stats:', error);
    }

    return user;
  }

  /**
   * FirebaseユーザーからアプリのUserモデルを作成
   */
  private createUserFromFirebaseUser(firebaseUser: FirebaseUser, nickname?: string): User {
    // 表示名を決定
    let displayName: string;
    if (firebaseUser.isAnonymous) {
      if (nickname) {
        // ニックネーム + #4桁のID
        const idSuffix = firebaseUser.uid.slice(-4);
        displayName = `${nickname} #${idSuffix}`;
      } else {
        // ニックネームなしの場合は「ゲスト」
        displayName = `ゲスト${firebaseUser.uid.slice(-6)}`;
      }
    } else if (firebaseUser.displayName) {
      displayName = firebaseUser.displayName;
    } else if (firebaseUser.email) {
      // Googleログインでも表示名がない場合はメールアドレスの@前を使用
      displayName = firebaseUser.email.split('@')[0];
    } else {
      displayName = `ユーザー${firebaseUser.uid.slice(-6)}`;
    }

    return {
      uid: firebaseUser.uid,
      displayName,
      email: firebaseUser.email || undefined,
      isAnonymous: firebaseUser.isAnonymous,
      createdAt: new Date(),
      lastLoginAt: new Date(),
      stats: {
        totalGames: 0,
        bestTime: null,
        recentTimes: [],
        averageTime10: null,
        averageTime30: null,
        averageTime100: null,
      },
    };
  }

  /**
   * 匿名認証でサインイン
   */
  async signInAnonymously(nickname?: string): Promise<User> {
    try {
      const credential: UserCredential = await firebaseSignInAnonymously(auth);
      const user = await this.loadUserWithStats(credential.user, nickname);
      this.currentUser = user;

      // セッション情報をlocalStorageにも保存（バックアップ）
      try {
        localStorage.setItem('chimdon_auth_uid', credential.user.uid);
        localStorage.setItem('chimdon_auth_type', 'anonymous');
        localStorage.setItem('chimdon_auth_time', new Date().toISOString());
        if (nickname) {
          localStorage.setItem('chimdon_auth_nickname', nickname);
        }
      } catch (e) {
        console.log('LocalStorage save failed:', e);
      }

      return user;
    } catch (error) {
      console.error('Anonymous sign in error:', error);
      throw error;
    }
  }

  /**
   * Googleアカウントでサインイン
   */
  async signInWithGoogle(): Promise<User> {
    try {
      const provider = new GoogleAuthProvider();
      const credential: UserCredential = await signInWithPopup(auth, provider);
      const user = await this.loadUserWithStats(credential.user);
      this.currentUser = user;

      // セッション情報をlocalStorageにも保存（バックアップ）
      try {
        localStorage.setItem('chimdon_auth_uid', credential.user.uid);
        localStorage.setItem('chimdon_auth_type', 'google');
        localStorage.setItem('chimdon_auth_time', new Date().toISOString());
      } catch (e) {
        console.log('LocalStorage save failed:', e);
      }

      return user;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  }

  /**
   * Twitter (X) アカウントでサインイン
   */
  async signInWithTwitter(): Promise<User> {
    try {
      const provider = new TwitterAuthProvider();
      const credential: UserCredential = await signInWithPopup(auth, provider);
      const user = await this.loadUserWithStats(credential.user);
      this.currentUser = user;

      // セッション情報をlocalStorageにも保存（バックアップ）
      try {
        localStorage.setItem('chimdon_auth_uid', credential.user.uid);
        localStorage.setItem('chimdon_auth_type', 'twitter');
        localStorage.setItem('chimdon_auth_time', new Date().toISOString());
      } catch (e) {
        console.log('LocalStorage save failed:', e);
      }

      return user;
    } catch (error) {
      console.error('Twitter sign in error:', error);
      throw error;
    }
  }

  /**
   * 匿名アカウントをGoogleアカウントにアップグレード
   */
  async upgradeToGoogle(): Promise<User> {
    if (!auth.currentUser || !auth.currentUser.isAnonymous) {
      throw new Error('Current user is not anonymous');
    }

    try {
      const provider = new GoogleAuthProvider();
      const credential = await linkWithPopup(auth.currentUser, provider);
      const user = await this.loadUserWithStats(credential.user);
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Account upgrade error:', error);
      throw error;
    }
  }

  /**
   * 匿名アカウントをTwitterアカウントにアップグレード
   */
  async upgradeToTwitter(): Promise<User> {
    if (!auth.currentUser || !auth.currentUser.isAnonymous) {
      throw new Error('Current user is not anonymous');
    }

    try {
      const provider = new TwitterAuthProvider();
      const credential = await linkWithPopup(auth.currentUser, provider);
      const user = await this.loadUserWithStats(credential.user);
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('Account upgrade error:', error);
      throw error;
    }
  }

  /**
   * サインアウト
   */
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
      this.currentUser = null;
      
      // LocalStorageもクリア
      try {
        localStorage.removeItem('chimdon_auth_uid');
        localStorage.removeItem('chimdon_auth_type');
        localStorage.removeItem('chimdon_auth_time');
      } catch (e) {
        console.log('LocalStorage clear failed:', e);
      }
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  /**
   * 現在のユーザーを取得
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * 認証済みかどうかチェック
   */
  isAuthenticated(): boolean {
    return auth.currentUser !== null;
  }

  /**
   * アカウントアップグレード可能かチェック
   */
  canUpgradeAccount(): boolean {
    return auth.currentUser?.isAnonymous === true;
  }
}